import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Button, Grid, InputAdornment, Box, OutlinedInput, InputLabel, FormControl, InputBase, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as SearchIconPlaceHolder } from "assets/icons/searchicon.svg";
import { ReactComponent as NoData } from "assets/My_Binnys_Access/NoData.svg";
import { ReactComponent as IndiaFlag } from "assets/My_Binnys_Access/indiaFlag.svg";
import apiClient from "lib/api-client";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatTrayName } from "utils/capitalEachLetter";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#FFFFFF",
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
  marginLeft: 0,
  width: "100%",
  boxShadow: "inset 0px 4px 20px 0px #00000040",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "black",
  height: "40px",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 1),
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "40ch",
    },
  },
}));

const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

const ClientModal = ({ open, onClose, onSuccess, type = "addClient", editTrayData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [buttonMode, setButtonMode] = useState("search");
  const [searchResult, setSearchResult] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [addUser, setAddUser] = useState({
    userName: "",
    mobileNumber: "",
    email: "",
  });
  const [showActive, setshowActive] = useState(addUser?.userName && addUser?.mobileNumber && addUser?.email);
  const [trayName, setTrayName] = useState("");
  const [eventName, setEventName] = useState("");
  const [clientList, setClientList] = useState([]);
  const [disable, setDisable] = useState(false);
  const inputRef = useRef();
  const { clientId } = useParams();

  const location = useLocation();

  const clientName = location?.state?.clientName;

  useEffect(() => {
    if (type === "addTray" && editTrayData) {
      setTrayName(editTrayData.trayName || "");
    } else if (type === "addEvent" && editTrayData) {
      setEventName(editTrayData.eventTypeName || "");
    } else if ((type === "addTray" || type === "addEvent") && !editTrayData) {
      setTrayName("");
      setEventName("");
    }
  }, [type, editTrayData, open]);

  // Client List Data

  const fetchClientSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setClientList([]);
      return;
    }
    try {
      const res = await apiClient.get(`/api/v9/myBinnys/getclientList?searchBy=${query}`);
      setClientList(res.data?.client || []);
    } catch (err) {
      console.error("Error fetching client list:", err);
      setClientList([]);
    }
  };

  const fetchClientName = async (mobileNumber) => {
    let filterNumber = `+91 ${mobileNumber}`;
    try {
      const response = await apiClient.post("/api/v1/catalogSetting/catalog-client/getClientName", { mobileNumber: filterNumber });

      if (response.status === 200) {
        const { clientName, clientEmail } = response?.data || {};
        setReadOnly(!!clientName);
        setAddUser((prev) => ({
          ...prev,
          userName: clientName?.charAt(0)?.toUpperCase() + clientName?.slice(1)?.toLowerCase(),
          email: clientEmail,
        }));
      }
    } catch (error) {
      console.error("Error fetching client name:", error);
    }
  };

  const debouncedFetchClientSuggestions = useMemo(() => debounce(fetchClientSuggestions, 300), []);
  // const debouncedFetchClientName = useMemo(() => debounce(fetchClientName, 300), []);

  // Single Client Data Show

  const handleClientSelect = async (client) => {
    try {
      const res = await apiClient.get(`/api/v9/myBinnys/getSingleClientDetail/${client?.clientCode}`);
      const data = res.data?.clientData;
      if (data) {
        setSearchResult({
          clientName: data?.clientName,
          clientCode: data?.partyCode,
          city: data?.city,
          state: data?.state,
          partyNo: data?.partyNo,
        });
        setClientList([]);
        setSearchTerm(`${client?.clientName} (${client?.clientCode})`);
        setButtonMode("clear");
      }
    } catch (err) {
      console.error("Error fetching client detail:", err);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm) return;
    fetchClientSuggestions(searchTerm);
  };

  useEffect(() => {
    const isActive = addUser?.userName?.trim() !== "" && addUser?.mobileNumber?.trim()?.length === 10 && addUser?.email?.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addUser?.email);
    setshowActive(isActive);
  }, [addUser]);

  const handleAddUserChange = (e) => {
    const { id, value } = e.target;
    setReadOnly(false);
    setAddUser((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setReadOnly(false);

    setAddUser((prev) => ({
      ...prev,
      mobileNumber: value,
      userName: "",
      email: ""
    }));

    if (value?.length > 9) {
      fetchClientName(value);
    }
  };

  // const handleSubmit = async () => {
  //   if (type === "addClient") {
  //     // Handle add Client case
  //     if (!searchResult) return;

  //     try {
  //       const response = await apiClient.post("/api/v9/myBinnys/visionClientcompany", {
  //         clientName: searchResult?.clientName,
  //         partyNo: searchResult?.partyNo,
  //         partyCode: searchResult?.clientCode,
  //       });

  //       // Handle success
  //       if (response.status === 200) {
  //         setSearchTerm("");
  //         setButtonMode("search");
  //         setSearchResult(null);
  //         onClose();
  //         onSuccess();
  //       }
  //     } catch (error) {
  //       console.error("Error submitting client:", error);
  //       if (error.response?.status === 400) {
  //         toast.error(error.response.data.message || "This client already exists");
  //       } else {
  //         alert("Failed to add client. Please try again.");
  //       }
  //     }
  //   } else if (type === "addTray") {
  //     if (!trayName.trim()) return;

  //     try {
  //       if (editTrayData?._id) {
  //         // Edit existing tray
  //         const response = await apiClient.put("/api/v1/tray/editTray", {
  //           trayId: editTrayData._id,
  //           trayName: trayName.trim().toLowerCase(),
  //         });

  //         if (response.status === 200) {
  //           onClose();
  //           onSuccess?.();
  //         }
  //       } else {
  //         const response = await apiClient.post("/api/v1/tray/addTray", {
  //           trayName: trayName.trim().toLowerCase(),
  //         });

  //         if (response.status === 201 || response.status === 200) {
  //           onClose();
  //           onSuccess?.();
  //         }
  //       }
  //     } catch (error) {
  //       console.error("Tray Error:", error);
  //       toast.error(error.response?.data?.message || "Failed to save tray. Please try again.");
  //     }
  //   } else if (type === "addEvent") {
  //     if (!eventName.trim()) return;

  //     try {
  //       if (editTrayData?._id) {
  //         // Edit existing tray
  //         const response = await apiClient.put("/api/v1/vision_events/edit", {
  //           eventTypeId: editTrayData._id,
  //           // eventTypeName: eventName.trim().toLowerCase(),
  //           eventTypeName: eventName.trim(),
  //         });

  //         if (response.status === 200) {
  //           onClose();
  //           onSuccess?.();
  //         }
  //       } else {
  //         const response = await apiClient.post("/api/v1/vision_events/add", {
  //           // eventTypeName: eventName.trim().toLowerCase(),
  //           eventTypeName: eventName.trim(),
  //         });

  //         if (response.status === 201 || response.status === 200) {
  //           onClose();
  //           onSuccess?.();
  //         }
  //       }
  //     } catch (error) {
  //       console.error("Event Type Error:", error);
  //       toast.error(error.response?.data?.message || "Failed to save eventType. Please try again.");
  //     }
  //   } else {
  //     // Handle add user case
  //     if (!showActive) return;

  //     try {
  //       const response = await apiClient.post("/api/v9/myBinnys/visionClientCompanyEmployee", {
  //         myBinnysClientCompanyRefs: `${clientId}`,
  //         clientName: addUser?.userName?.trim(),
  //         clientNumber: `+91 ${addUser?.mobileNumber?.trim()}`,
  //         clientEmail: addUser?.email?.trim(),
  //       });

  //       if (response.status === 200) {
  //         // Reset form and close modal
  //         setAddUser({
  //           userName: "",
  //           mobileNumber: "",
  //           email: "",
  //         });
  //         onClose();
  //         onSuccess();
  //       }
  //     } catch (error) {
  //       console.error("Error adding user:", error);
  //       toast.error(error.response?.data?.message || "Failed to add user. Please try again.");
  //     }
  //   }
  // };

  const handleSubmit = async () => {
    if (disable) return;
    setDisable(true);
  
    try {
      if (type === "addClient") {
        if (!searchResult) return setDisable(false);
  
        const response = await apiClient.post("/api/v9/myBinnys/visionClientcompany", {
          clientName: searchResult?.clientName,
          partyNo: searchResult?.partyNo,
          partyCode: searchResult?.clientCode,
        });
  
        if (response.status === 200) {
          setSearchTerm("");
          setButtonMode("search");
          setSearchResult(null);
          onClose();
          onSuccess();
        }
  
      } else if (type === "addTray") {
        if (!trayName.trim()) return setDisable(false);
  
        const payload = { trayName: trayName.trim().toLowerCase() };
  
        const response = editTrayData?._id
          ? await apiClient.put("/api/v1/tray/editTray", {
              trayId: editTrayData._id,
              ...payload,
            })
          : await apiClient.post("/api/v1/tray/addTray", payload);
  
        if (response.status === 200 || response.status === 201) {
          onClose();
          onSuccess?.();
        }
  
      } else if (type === "addEvent") {
        if (!eventName.trim()) return setDisable(false);
  
        const payload = { eventTypeName: eventName.trim() };
  
        const response = editTrayData?._id
          ? await apiClient.put("/api/v1/vision_events/edit", {
              eventTypeId: editTrayData._id,
              ...payload,
            })
          : await apiClient.post("/api/v1/vision_events/add", payload);
  
        if (response.status === 200 || response.status === 201) {
          onClose();
          onSuccess?.();
        }
  
      } else {
        if (!showActive) return setDisable(false);
  
        const response = await apiClient.post("/api/v9/myBinnys/visionClientCompanyEmployee", {
          myBinnysClientCompanyRefs: `${clientId}`,
          clientName: addUser?.userName?.trim(),
          clientNumber: `+91 ${addUser?.mobileNumber?.trim()}`,
          clientEmail: addUser?.email?.trim(),
        });
  
        if (response.status === 200) {
          setAddUser({ userName: "", mobileNumber: "", email: "" });
          onClose();
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Submit Error:", error);
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setDisable(false);
    }
  };

  const renderAddClient = () => (
    <>
      <Search sx={{ display: "flex", alignItems: "center", paddingLeft: "10px", paddingRight: "4px", marginLeft: "0 !important", width: "100%" }}>
        <SearchIconPlaceHolder sx={{ color: "gray", marginRight: "8px" }} />
        <Box sx={{ flexGrow: 1, position: "relative" }}>
          <StyledInputBase
            inputRef={inputRef}
            placeholder="Search Client name"
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              debouncedFetchClientSuggestions(val);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // debouncedFetchClientSuggestions(e.target.value);
                handleSearch();
                setButtonMode("clear");
                handleClientSelect(clientList[0]);
              }
            }}
            value={searchTerm}
            sx={{ height: "40px", borderRadius: "5px", width: "100%" }}
          />

          {clientList.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                width: "554px",
                right: "-85px",
                top: "42px",
                zIndex: 20,
                maxHeight: 200,
                overflowY: "auto",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e0e0e0",
                backgroundColor: "white",
                padding: "3px",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {clientList.map((client) => (
                <Box
                  key={client?.clientCode}
                  onClick={() => handleClientSelect(client)}
                  sx={{
                    padding: "10px",
                    fontSize: "14px",
                    cursor: "pointer",
                    fontFamily: "Poppins",
                    color: "black",
                    backgroundColor: "white",
                    "&:hover": { color: "white", backgroundColor: "#444444", borderRadius: "5px" },
                  }}
                >
                  {client?.clientName} {/* Removed the client code */}
                </Box>
              ))}
            </Paper>
          )}
        </Box>

        <IconButton
          disableRipple
          disableFocusRipple
          sx={{
            height: "34px",
            width: "69px",
            borderRadius: "5px",
            padding: "10px",
            background: "#000",
            fontFamily: "Poppins",
            color: "#fff",
            fontWeight: "500",
            fontSize: "12px",
            marginLeft: "10px",
          }}
          onClick={() => {
            if (buttonMode === "search") {
              handleSearch();
              setButtonMode("clear");
            } else {
              setSearchTerm("");
              setButtonMode("search");
              setSearchResult(null);
              setClientList([]);
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }}
        >
          {buttonMode === "search" ? "Search" : "Clear"}
        </IconButton>
      </Search>

      {!searchResult ? (
        <Box sx={{ textAlign: "center", padding: "40px 0" }}>
          <NoData />
          <div style={{ color: "#999", fontSize: "14px" }}>Please, select Client</div>
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ marginTop: "10px" }}>
          {["clientName", "clientCode", "city", "state"].map((field) => {
            const labelText = field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
            return (
              <Grid item xs={6} key={field}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    shrink
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "16px",
                      fontFamily: "Poppins",
                      fontWeight: 300,
                      mb: "6px",
                      transform: "none",
                    }}
                  >
                    {labelText}
                  </InputLabel>
                  <OutlinedInput
                    readOnly
                    value={searchResult[field] || ""}
                    sx={{
                      backgroundColor: "#101010",
                      marginTop: "30px",
                      color: "#FFFFFF",
                      borderRadius: "6px",
                      height: "50px",
                      fontSize: "16px",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      px: 1.5,
                    }}
                  />
                </FormControl>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );

  const renderAddUser = () => (
    <Grid container spacing={2} mt={1}>
      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined">
          <InputLabel
            htmlFor="mobileNumber"
            shrink
            sx={{
              color: "#FFFFFF",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 300,
              mb: "6px",
              position: "relative",
              transform: "none",
              "&.Mui-focused": { color: "#FFFFFF" },
            }}
          >
            Mobile No.
          </InputLabel>
          <OutlinedInput
            id="mobileNumber"
            placeholder="Mobile No."
            type="tel"
            value={addUser.mobileNumber}
            onChange={handleMobileNumberChange}
            inputProps={{ maxLength: 10 }}
            startAdornment={
              <InputAdornment position="start" sx={{ color: "#FFFFFF", mr: 1 }}>
                <IndiaFlag /> <span style={{ marginLeft: "6px" }}>+91 |</span>
              </InputAdornment>
            }
            notched={false}
            sx={{
              backgroundColor: "#101010",
              color: "#FFFFFF",
              borderRadius: "6px",
              height: "50px",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 500,
              px: 1.5,
              "& input": { padding: 0, ml: 1 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
            }}
            // onKeyDown={(e) => {
            //   if (!/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(e.key)) {
            //     e.preventDefault();
            //   }
            // }}
            // onPaste={(e) => {
            //   const pasteData = e.clipboardData.getData("text");
            //   if (!/^\d*$/.test(pasteData)) e.preventDefault();
            // }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined">
          <InputLabel
            htmlFor="userName"
            shrink
            sx={{
              color: "#FFFFFF",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 300,
              mb: "6px",
              position: "relative",
              transform: "none",
              "&.Mui-focused": { color: "#FFFFFF" },
            }}
          >
            User Name
          </InputLabel>
          <OutlinedInput
            id="userName"
            placeholder="Enter Name"
            value={addUser.userName}
            readOnly={readOnly}
            onChange={handleAddUserChange}
            notched={false}
            sx={{
              backgroundColor: "#101010",
              color: "#FFFFFF",
              borderRadius: "6px",
              height: "50px",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 500,
              px: 1.5,
              "& input": { padding: 0 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
            }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined">
          <InputLabel
            htmlFor="email"
            shrink
            sx={{
              color: "#FFFFFF",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 300,
              mb: "6px",
              position: "relative",
              transform: "none",
              "&.Mui-focused": { color: "#FFFFFF" },
            }}
          >
            Email
          </InputLabel>
          <OutlinedInput
            id="email"
            placeholder="Enter Email"
            value={addUser.email}
            onChange={handleAddUserChange}
            notched={false}
            sx={{
              backgroundColor: "#101010",
              color: "#FFFFFF",
              borderRadius: "6px",
              height: "50px",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 500,
              px: 1.5,
              "& input": { padding: 0 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
            }}
          />
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderAddTray = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <label style={{ color: "#fff", fontSize: "16px", fontFamily: "Poppins", fontWeight: 300, lineHeight: "100%" }}>Tray Name</label>

      <input
        type="text"
        value={trayName}
        onChange={(e) => setTrayName(e.target.value)}
        placeholder="Enter tray name"
        style={{
          background: "transparent",
          border: "1px solid #8B8B8B",
          borderRadius: "4px",
          padding: "10px",
          color: "#fff",
          fontSize: "14px",
          outline: "none",
          width: "100%",
        }}
      />
    </div>
  );

  const renderAddEvent = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <label style={{ color: "#fff", fontSize: "16px", fontFamily: "Poppins", fontWeight: 300, lineHeight: "100%" }}>Event Name</label>

      <input
        type="text"
        value={eventName}
        onChange={(e) => setEventName(e.target.value)}
        placeholder="Enter event name"
        style={{
          background: "transparent",
          border: "1px solid #8B8B8B",
          borderRadius: "4px",
          padding: "10px",
          color: "#fff",
          fontSize: "14px",
          outline: "none",
          width: "100%",
        }}
      />
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          background: "#111",
          color: "#fff",
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#8B8B8B",
          paddingBottom: "18px",
          fontFamily: "Poppins",
          fontSize: "18px",
          fontWeight: 500,
        }}
      >
        {/* {type === "addClient" ? "Add Client" : `Add User to ${clientName}`} */}
        {type === "addClient" && "Add Client"}
        {type !== "addClient" && type !== "addTray" && type !== "addEvent" && `Add User to ${clientName}`}
        {type === "addTray" && (editTrayData ? "Edit Tray" : "Add Tray")}
        {type === "addEvent" && (editTrayData ? "Edit Event" : "Add Event")}
        <IconButton
          sx={{ width: "32px", height: "32px", border: "1px solid #8B8B8B", borderRadius: "4px" }}
          onClick={() => {
            setAddUser({
              userName: "",
              mobileNumber: "",
              email: "",
            });
            setSearchTerm("");
            setButtonMode("search");
            setSearchResult(null);
            onClose();
          }}
        >
          <CloseIcon sx={{ width: "16px", height: "16px", color: "#8B8B8B" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "0 24px",
          overflow: "hidden",
          "&.MuiDialogContent-root": { paddingTop: "8px" },
        }}
      >
        {/* {type === "addClient" ? renderAddClient() : renderAddUser()} */}
        {type === "addClient" && renderAddClient()}
        {type !== "addClient" && type !== "addTray" && type !== "addEvent" && renderAddUser()}
        {type === "addTray" && renderAddTray()}
        {type === "addEvent" && renderAddEvent()}

        <Box
          sx={{
            background: "#191818",
            padding: "16px 0",
            margin: "24px -24px 0",
            textAlign: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              // backgroundColor: type === "addClient" && searchResult ? "#50C878" : showActive ? "#50C878" : "#8B8B8B !important",
              backgroundColor:
                (type === "addClient" && searchResult) || (type === "addTray" && trayName.trim()) || (type === "addEvent" && eventName.trim()) || showActive ? "#50C878" : "#8B8B8B !important",
              padding: "8px 32px",
              fontWeight: 700,
              fontFamily: "Poppins",
              borderRadius: "6px",
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "-0.41px",
              color: "#000000 !important",
              "&:hover": {
                backgroundColor: type === "addClient" && searchResult ? "#50C878" : "#50C878",
              },
              cursor: (type === "addClient" ? searchResult : showActive) || type === "addEvent" ? "pointer" : "default",
            }}
            onClick={handleSubmit}
            disabled={
              disable ||
              (type === "addClient" && !searchResult) ||
              (type === "addTray" && !trayName.trim()) ||
              (!showActive && type !== "addClient" && type !== "addTray" && type !== "addEvent") ||
              (type === "addEvent" && !eventName.trim())
            }
          >
            {type === "addTray" || type === "addEvent" ? "SAVE" : "SUBMIT"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ClientModal;
